import { useLocation } from 'react-router';
import { selectedClient } from '@/core/selectedClient';
import { filesNothingAtCompaniesHouse } from '@/core/useClientType';
import {
  ClientKind,
  resetClientKindCache,
  setClientCommunityInterest,
  useClientKind,
} from '@/features/tr-reportsettings/clientKind';
import { COMPANIES_HOUSE_MENU_LABEL, useCompaniesHouseMenuLink } from '@/features/tr-mgmt/CompaniesHouseCompanyLink';
import { ExternalSubMenuLink, SideMenu, SubMenuLink, isItemActive } from './SideMenu';
import {
  CIC_REPORTS_MENU_ITEM,
  CIC_SUBMISSION_MENU_ITEM,
  INTEGRATION_MENU,
  LOGS_MENU,
  REPORTS_MENU,
  SETTINGS_MENU,
  TASK_MENU,
} from './menuItems';

export interface MenuProps {
  closeMenu: boolean;
  onToggle: () => void;
}

/** BUG-062: a community interest company's menus carry its CIC Reports list and CIC Submission Wizard. */
function useIsCommunityInterest(): boolean {
  return !!useClientKind(selectedClient()?.id)?.isCommunityInterest;
}

/**
 * Limited-company screens that do not apply to sole traders, partnerships and LLPs: the group screens. Tax Handoff is NOT one
 * of them (Testing Flows): every client type ends there, handing to Corporation Tax or, for a sole trader, partnership or LLP,
 * to Self Assessment (an LLP's members are taxed on their shares, not the LLP).
 */
const LIMITED_ONLY_TASKS = ['tasks-groupstructure', 'tasks-grouptrialbalance', 'tasks-groupjournals'].map(
  (route) => `/home/taxreturns/${route}`,
);

/** Profit allocation and partner accounts apply to partnerships and LLPs only. */
const PARTNERSHIP_ONLY_TASKS = ['tasks-profitallocation', 'tasks-partneraccounts'].map(
  (route) => `/home/taxreturns/${route}`,
);

/** Sole traders and partnerships do not file accounts at Companies House, so they have no Submit screen either. */
const NO_FILING_HIDDEN_TASK = '/home/taxreturns/tasks-submit';

/** Settings pages a sole trader or partnership does not have: revised accounts. */
const NO_REVISION_HIDDEN_SETTINGS = ['revised-accounts', 'revisions-lineage'];

export function TaskMenu(props: MenuProps) {
  const isCic = useIsCommunityInterest();
  const clientType = useClientKind(selectedClient()?.id)?.clientType;
  // Until the type is known (or if it cannot be read) the menu is shown as for a limited company, so the
  // partnership-only items stay hidden and the limited-only ones stay visible.
  const isPartnershipOrLlp = clientType === ClientKind.Partnership || clientType === ClientKind.Llp;
  const isLimited = !clientType;
  const visible = TASK_MENU.filter((i) => {
    if (PARTNERSHIP_ONLY_TASKS.includes(i.to)) return isPartnershipOrLlp;
    if (!isLimited && LIMITED_ONLY_TASKS.includes(i.to)) return false;
    // APT353: iXBRL Tagging and Filing Status are inside Compliance & Filing (its top bar leaves them out for a client that files nothing).
    return !(filesNothingAtCompaniesHouse(clientType) && i.to === NO_FILING_HIDDEN_TASK);
  });
  // Oct-10 #112 (CIC 86365): as in 3.0, a CIC files through the CIC Submission Wizard, which takes the plain Submit's place.
  const items = isCic ? visible.map((i) => (i.to.endsWith('/tasks-submit') ? CIC_SUBMISSION_MENU_ITEM : i)) : visible;
  return <SideMenu items={items} {...props} />;
}

export function ReportsMenu(props: MenuProps) {
  const client = selectedClient();
  const kind = useClientKind(client?.id);
  const isCic = !!kind?.isCommunityInterest;
  const isSoleTrader =
    kind?.clientType === ClientKind.SoleTrader || Boolean(client?.name?.toLowerCase().includes('soletrader'));

  let items = REPORTS_MENU;
  if (isSoleTrader) {
    // In Capium 3.0, a Sole Trader client's Reports menu offers Sole Trader Accounts, Trial Balance, and Nominal Ledger.
    const byRoute = (route: string) => REPORTS_MENU.find((item) => item.to.endsWith(`/${route}`))!;
    items = [
      { ...byRoute('reports-accounts'), label: 'Sole Trader Accounts' },
      byRoute('reports-trialbalance'),
      byRoute('reports-ladger'),
    ];
  } else if (kind?.clientType === ClientKind.Partnership) {
    // BUG-078: a partnership's annual accounts are its "Partnership Accounts".
    items = REPORTS_MENU.map((item) =>
      item.to.endsWith('/reports-accounts') ? { ...item, label: 'Partnership Accounts' } : item,
    );
  }
  return <SideMenu items={isCic ? [...items, CIC_REPORTS_MENU_ITEM] : items} {...props} />;
}

/** APT363: "View on Companies House" is the first item, then Company Officers, Per. with Sig. Control and Filing History. */
export function IntegrationMenu(props: MenuProps) {
  const { pathname } = useLocation();
  const companiesHouse = useCompaniesHouseMenuLink();
  return (
    <SideMenu items={INTEGRATION_MENU} {...props}>
      {!companiesHouse.hidden && (
        <ExternalSubMenuLink
          href={companiesHouse.url}
          icon="open_in_new"
          label={COMPANIES_HOUSE_MENU_LABEL}
          disabledReason={companiesHouse.reason}
          collapsed={!props.closeMenu}
        />
      )}
      {INTEGRATION_MENU.map((item) => (
        <SubMenuLink
          key={item.to}
          to={item.to}
          icon={item.icon}
          label={item.label}
          active={isItemActive(pathname, item)}
          collapsed={!props.closeMenu}
        />
      ))}
    </SideMenu>
  );
}
export const LogsMenu = (props: MenuProps) => <SideMenu items={LOGS_MENU} {...props} />;

/** BUG-027: Company Info records a changed CIC flag in the client-kind cache the menu reads. */
export { setClientCommunityInterest };

export function resetCommunityInterestCache(): void {
  resetClientKindCache();
}

/**
 * The Settings menu (APT371, APT372): single entries in the client's order. Report Settings is one entry (its pages are the top bar across each of
 * them), and Revised Accounts is one entry (its two screens are the tabs); a sole trader or partnership has no Revised Accounts.
 */
export function SettingsMenu(props: MenuProps) {
  // BUG-069/072/078: what the menu offers follows the client's type.
  const kind = useClientKind(selectedClient()?.id);
  const clientType = kind?.clientType ?? ClientKind.LimitedCompany;
  const noRevisions = clientType === ClientKind.SoleTrader || clientType === ClientKind.Partnership;
  const items = noRevisions
    ? SETTINGS_MENU.filter((item) => !NO_REVISION_HIDDEN_SETTINGS.some((route) => item.to.endsWith(`/${route}`)))
    : SETTINGS_MENU;
  return <SideMenu items={items} {...props} />;
}
